import { useApolloClient, ApolloError } from '@apollo/client';
import { updateShowCache, updateSubscriptionCache, updateUserCache } from 'src/apollo/cache/update';
import { useNavigate } from 'react-router-dom';
import { useState, ReactNode, useEffect } from 'react';
import { useLoginMutation } from 'src/generated/graphql';
import SupportLink from 'src/components/intercom/SupportLink';
import { Text } from '@chakra-ui/react';
import { usePermissionsContext } from 'src/components/permissions/PermissionsContext';

interface LoginVariables {
  email: string;
  password: string;
}

export default function useLogin() {
  const navigate = useNavigate();
  const client = useApolloClient();
  const [_login] = useLoginMutation();
  const [submissionError, setSubmissionError] = useState<ReactNode | string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { permissions, refetchPermissions } = usePermissionsContext();

  useEffect(() => {
    if (permissions?.tier) {
      navigate('/episodes');
    }
  }, [permissions?.tier, navigate]);

  const login = (variables: LoginVariables) => {
    setIsSubmitting(true);
    setSubmissionError(null);
    return _login({ variables })
      .then(res => {
        if (res.data) {
          updateUserCache({ client, user: res.data.login.user });
          updateShowCache({ client, show: res.data.login.show });

          // There are edge cases when signup fails in the payment stage so subscription doesn't exist
          // in that case we have no subscription to update cache with
          if (res.data.login.subscription) {
            updateSubscriptionCache({ client, subscription: res.data.login.subscription });
          }
          localStorage.setItem('show-mobile-warning', 'true');
          if (res.data.login.user?.isAuthenticated) {
            localStorage.setItem('has-alitu-account', 'true');
            refetchPermissions();
          }
        }
      })
      .catch((err: ApolloError) => {
        setIsSubmitting(false);
        const isValidationError = err?.graphQLErrors?.some(({ message }) =>
          message.includes('Error validating user'),
        );
        if (isValidationError) {
          setSubmissionError("We couldn't match the username and password");
        } else {
          setSubmissionError(
            <Text fontSize="inherit" color="inherit">
              Sorry, we couldn&apos;t log you in. Please{' '}
              <SupportLink
                message={`Help! I can't log in. I get an error message "${err.message}"`}
                color="inherit"
              >
                contact support
              </SupportLink>
              . {err.message}
            </Text>,
          );
        }
      });
  };

  return { login, submissionError, isSubmitting };
}
